<template>
  <div class="wapers-opinion-page">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderPage :page="currentPageInfo" />
    <div class="order">
      <section
        v-if="metaContent.value?.description"
        class="section order-last"
      >
        <div
          class="description"
          v-html="metaContent.value.description"
        />
      </section>
      <div class="order-first">
        <section
          v-if="userWaperOpinions.length && authenticated"
          class="my-cards section"
        >
          <div class="title-line">
            <h2 class="title-strat">Derniers avis sur vos communautés</h2>
          </div>
          <FilterMain
            :current="filterGame"
            :items="myCardsFilterItems"
            :action="(slug) => (filterGame = slug)"
          />
          <div class="slider">
            <CardSlider :cards="myCardsFiltered" />
          </div>
        </section>
        <SearchResults
          :is-loading="isLoading"
          placeholder-text="Rechercher un avis de joueur"
          @search-update="searchUpdateHandler"
        >
          <template #title>
            <h2 class="title-strat">
              {{
                authenticated && !!userWaperOpinions.length
                  ? "Plus d'avis de joueurs"
                  : currentPageInfo.title
              }}
            </h2>
          </template>
          <template #results>
            <CardContainer
              :items="waperOpinions"
              :no-result-text="noContent"
            />
          </template>
        </SearchResults>
        <ThePagination
          :total-pages="waperOpinionsPages"
          :current-page="waperOpinionsCurrentPage"
          :go-to="changePageTo"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadOpinions = useLoadOpinions()
    return loadOpinions(to)
  },
})

const route = useRoute()

const { authenticated } = useAuthenticated()

const filterGame = ref('all')

const waperOpinionsStore = useWaperOpinionsStore()
const metaContentStore = useMetaContentStore()

const {
  waperOpinions,
  userWaperOpinions,
  waperOpinionsPages,
  waperOpinionsCurrentPage,
} = storeToRefs(waperOpinionsStore)

const link = useLinkPrevNext({
  currentPage: waperOpinionsCurrentPage,
  pagesCount: waperOpinionsPages,
})

const arianeRoute = ref([
  {
    title: 'Tous les avis des joueurs',
    route: '',
  },
])

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: 'Tous les avis de joueurs',
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: 'Tous les avis de joueurs We Are PlayStation',
  description:
    'Découvrez tous les avis des joueurs de la communauté We Are PlayStation.',
  ogTitle: 'Tous les avis de joueurs We Are PlayStation',
  ogDescription:
    'Découvrez tous les avis des joueurs de la communauté We Are PlayStation.',
  ogImage: 'https://www.weareplaystation.fr/img/tous-les-avis-de-joueurs.jpg',
})

const currentPageInfo = {
  title: 'Tous les avis de joueurs',
  image: '/img/tous-les-avis-de-joueurs.jpg',
  titleSmall: 'tous les',
  titleBig: 'avis de joueurs',
  description:
    'Découvrez tous les avis des joueurs de la communauté We Are PlayStation.',
}

const metaContent = computed(() => {
  return metaContentStore.getTransverseBySlug(route.name)
})

const myCardsFiltered = computed(() => {
  if (filterGame.value === 'all') {
    return userWaperOpinions.value
  }

  return userWaperOpinions.value.filter(
    (card) => card.community?.slug === filterGame.value,
  )
})

const myCardsFilterItems = computed(() => {
  return (
    userWaperOpinions.value
      .map((card) => ({ ...card.community }))
      .filter(
        (item, index, arr) =>
          arr.findIndex((el) => item.slug === el.slug) === index,
      ) || []
  )
})

const searchData = async () => {
  isLoading.value = true

  await waperOpinionsStore.fetchWaperOpinions({ page: 1, search: search.value })

  if (!waperOpinions.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const loadOpinions = useLoadOpinions()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadOpinions(route)
  }
})

const changePageTo = async (index = 1) => {
  if (index > 0) {
    await waperOpinionsStore.fetchWaperOpinions({
      page: index,
      search: search.value,
    })
  }
}
</script>

<style lang="scss" scoped>
.wapers-opinion-page {
  min-height: 100vh;
  padding-bottom: 7rem;

  @media screen and (min-width: 700px) {
    padding-bottom: 10rem;
  }

  .my-cards {
    @media screen and (max-width: 700px) {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }
  }

  .title-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 2rem;
  }
}

.order {
  display: flex;
  flex-direction: column;

  &-first {
    order: 1;
  }

  &-last {
    order: 2;
    width: 100%;
  }

  .description {
    &:deep(h2) {
      margin-bottom: 1.5rem;
    }

    &:deep(h3) {
      margin-bottom: 1rem;
      font-family: SST;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 2rem;
      letter-spacing: 0.225rem;
      text-transform: uppercase;
    }

    &:deep(p) {
      margin-bottom: 1rem;
    }

    &:deep(ul) {
      margin-bottom: 1rem;
      padding-left: 1em;
      list-style: disc;
    }
  }
}
</style>
